import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";

import { auth, db } from "../firebase";

function EmployeeDashboard() {
  const navigate = useNavigate();

  const [employee, setEmployee] =
    useState(null);

  const [leads, setLeads] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [
    attendanceLoading,
    setAttendanceLoading,
  ] = useState(false);

  const [
    attendanceMessage,
    setAttendanceMessage,
  ] = useState("");

  const [
    currentTime,
    setCurrentTime,
  ] = useState(
    new Date()
  );

  const [
    workStatus,
    setWorkStatus,
  ] = useState(
    "Not Started"
  );

  const [
    workStartedAt,
    setWorkStartedAt,
  ] = useState("");

  const [
    workEndedAt,
    setWorkEndedAt,
  ] = useState("");

  const [
    breakStartedAt,
    setBreakStartedAt,
  ] = useState("");

  const [
    totalBreakSeconds,
    setTotalBreakSeconds,
  ] = useState(0);

  const [
    lastActivityAt,
    setLastActivityAt,
  ] = useState("");

  /*
    LOCAL DATE

    We use local date instead of UTC
    for attendance history.
  */

  const getLocalDateKey =
    useCallback(
      (
        value =
          new Date()
      ) => {
        const date =
          value instanceof
          Date
            ? value
            : new Date(
                value
              );

        if (
          Number.isNaN(
            date.getTime()
          )
        ) {
          return "";
        }

        const year =
          date.getFullYear();

        const month =
          String(
            date.getMonth() +
              1
          ).padStart(
            2,
            "0"
          );

        const day =
          String(
            date.getDate()
          ).padStart(
            2,
            "0"
          );

        return `${year}-${month}-${day}`;
      },
      []
    );

  const getAttendanceStorageKey =
    useCallback(
      (
        employeeId
      ) =>
        `crmAttendance_${employeeId}`,
      []
    );

  const saveAttendanceLocally =
    useCallback(
      (
        employeeId,
        attendanceData
      ) => {
        if (
          !employeeId
        ) {
          return;
        }

        const key =
          getAttendanceStorageKey(
            employeeId
          );

        let oldData =
          {};

        try {
          const saved =
            localStorage.getItem(
              key
            );

          oldData =
            saved
              ? JSON.parse(
                  saved
                )
              : {};
        } catch (
          error
        ) {
          oldData =
            {};
        }

        localStorage.setItem(
          key,
          JSON.stringify({
            ...oldData,
            ...attendanceData,
          })
        );
      },
      [
        getAttendanceStorageKey,
      ]
    );

  const applyAttendanceState =
    useCallback(
      (
        attendanceData
      ) => {
        const allowed =
          [
            "Not Started",
            "Working",
            "On Break",
            "Work Ended",
          ];

        const status =
          allowed.includes(
            attendanceData.workStatus
          )
            ? attendanceData.workStatus
            : "Not Started";

        setWorkStatus(
          status
        );

        setWorkStartedAt(
          attendanceData.workStartedAt ||
            ""
        );

        setWorkEndedAt(
          attendanceData.workEndedAt ||
            ""
        );

        setBreakStartedAt(
          attendanceData.breakStartedAt ||
            ""
        );

        setTotalBreakSeconds(
          Number(
            attendanceData.totalBreakSeconds
          ) || 0
        );

        setLastActivityAt(
          attendanceData.lastActivityAt ||
            ""
        );
      },
      []
    );

  /*
    LOAD EMPLOYEE + LEADS
  */

  useEffect(() => {
    const savedEmployee =
      localStorage.getItem(
        "crmEmployee"
      );

    if (
      !savedEmployee
    ) {
      navigate(
        "/employee-login"
      );

      return;
    }

    let parsedEmployee;

    try {
      parsedEmployee =
        JSON.parse(
          savedEmployee
        );
    } catch (
      error
    ) {
      localStorage.removeItem(
        "crmEmployee"
      );

      navigate(
        "/employee-login"
      );

      return;
    }

    if (
      !parsedEmployee?.id ||
      !parsedEmployee?.email
    ) {
      localStorage.removeItem(
        "crmEmployee"
      );

      navigate(
        "/employee-login"
      );

      return;
    }

    setEmployee(
      parsedEmployee
    );

    /*
      First show locally saved attendance.
      This prevents temporary wrong status
      while Firebase is loading.
    */

    const localAttendance =
      localStorage.getItem(
        getAttendanceStorageKey(
          parsedEmployee.id
        )
      );

    if (
      localAttendance
    ) {
      try {
        applyAttendanceState(
          JSON.parse(
            localAttendance
          )
        );
      } catch (
        error
      ) {
        console.error(
          "Local attendance load error:",
          error
        );
      }
    }

    const loadDashboard =
      async () => {
        try {
          setLoading(
            true
          );

          const employeeReference =
            doc(
              db,
              "employees",
              parsedEmployee.id
            );

          const cleanEmail =
            parsedEmployee.email
              .trim()
              .toLowerCase();

          const leadQuery =
            query(
              collection(
                db,
                "leads"
              ),
              where(
                "assignedEmployeeEmail",
                "==",
                cleanEmail
              )
            );

          const [
            employeeSnapshot,
            leadsSnapshot,
          ] =
            await Promise.all([
              getDoc(
                employeeReference
              ),

              getDocs(
                leadQuery
              ),
            ]);

          if (
            employeeSnapshot.exists()
          ) {
            const data =
              employeeSnapshot.data();

            const updatedEmployee =
              {
                ...parsedEmployee,
                ...data,
                id:
                  parsedEmployee.id,
              };

            setEmployee(
              updatedEmployee
            );

            localStorage.setItem(
              "crmEmployee",
              JSON.stringify(
                updatedEmployee
              )
            );

            const attendanceData =
              {
                workStatus:
                  data.workStatus ||
                  "Not Started",

                workStartedAt:
                  data.workStartedAt ||
                  "",

                workEndedAt:
                  data.workEndedAt ||
                  "",

                breakStartedAt:
                  data.breakStartedAt ||
                  "",

                totalBreakSeconds:
                  Number(
                    data.totalBreakSeconds
                  ) || 0,

                lastActivityAt:
                  data.lastActivityAt ||
                  "",
              };

            applyAttendanceState(
              attendanceData
            );

            saveAttendanceLocally(
              parsedEmployee.id,
              attendanceData
            );
          }

          const leadList =
            leadsSnapshot.docs.map(
              (
                leadDocument
              ) => ({
                id:
                  leadDocument.id,

                ...leadDocument.data(),
              })
            );

          setLeads(
            leadList
          );
        } catch (
          error
        ) {
          console.error(
            "Employee dashboard load error:",
            error
          );

          setAttendanceMessage(
            "Unable to load your assigned data."
          );
        } finally {
          setLoading(
            false
          );
        }
      };

    loadDashboard();
  }, [
    navigate,
    applyAttendanceState,
    getAttendanceStorageKey,
    saveAttendanceLocally,
  ]);

  /*
    LIVE CLOCK
  */

  useEffect(() => {
    const timer =
      setInterval(
        () => {
          setCurrentTime(
            new Date()
          );
        },
        1000
      );

    return () =>
      clearInterval(
        timer
      );
  }, []);

  /*
    MAIN ATTENDANCE UPDATE

    This employees document is the MAIN
    source of truth.

    Start/End buttons depend on this,
    not attendanceLogs.
  */

  const updateEmployeeAttendance =
    useCallback(
      async (
        attendanceData
      ) => {
        if (
          !employee?.id
        ) {
          throw new Error(
            "Employee session missing."
          );
        }

        await updateDoc(
          doc(
            db,
            "employees",
            employee.id
          ),
          {
            ...attendanceData,

            attendanceUpdatedAt:
              serverTimestamp(),
          }
        );

        saveAttendanceLocally(
          employee.id,
          attendanceData
        );
      },
      [
        employee,
        saveAttendanceLocally,
      ]
    );

  /*
    SAFE HISTORY SAVE

    IMPORTANT FIX:

    If attendanceLogs permission/history
    saving fails, Start Work / End Work
    should still remain successful.
  */

  const saveAttendanceHistory =
    async (
      {
        status,
        activityTime,
        breakRecord =
          null,
        finalBreakSeconds =
          0,
      }
    ) => {
      if (
        !employee?.id
      ) {
        return;
      }

      try {
        const dateKey =
          getLocalDateKey(
            workStartedAt ||
              activityTime
          );

        const historyReference =
          doc(
            db,
            "attendanceLogs",
            `${employee.id}_${dateKey}`
          );

        let existing =
          {};

        try {
          const snapshot =
            await getDoc(
              historyReference
            );

          if (
            snapshot.exists()
          ) {
            existing =
              snapshot.data();
          }
        } catch (
          error
        ) {
          /*
            Reading old history failed.
            We can still attempt merge write.
          */
        }

        const previousBreaks =
          Array.isArray(
            existing.breaks
          )
            ? existing.breaks
            : [];

        const breaks =
          breakRecord
            ? [
                ...previousBreaks,
                breakRecord,
              ]
            : previousBreaks;

        const start =
          existing.workStartedAt ||
          workStartedAt ||
          activityTime;

        let effectiveSeconds =
          Number(
            existing.effectiveWorkSeconds
          ) || 0;

        if (
          status ===
          "Work Ended"
        ) {
          const elapsed =
            Math.max(
              0,
              Math.floor(
                (
                  new Date(
                    activityTime
                  ).getTime() -
                  new Date(
                    start
                  ).getTime()
                ) /
                  1000
              )
            );

          effectiveSeconds =
            Math.max(
              0,
              elapsed -
                finalBreakSeconds
            );
        }

        await setDoc(
          historyReference,
          {
            employeeId:
              employee.id,

            employeeName:
              employee.name ||
              "Employee",

            employeeEmail:
              employee.email ||
              "",

            date:
              dateKey,

            workStartedAt:
              start,

            workEndedAt:
              status ===
              "Work Ended"
                ? activityTime
                : existing.workEndedAt ||
                  "",

            status,

            breaks,

            breakCount:
              breaks.length,

            totalBreakSeconds:
              finalBreakSeconds,

            effectiveWorkSeconds:
              effectiveSeconds,

            activeBreakStartedAt:
              status ===
              "On Break"
                ? activityTime
                : "",

            lastActivityAt:
              activityTime,

            updatedAt:
              serverTimestamp(),
          },
          {
            merge:
              true,
          }
        );
      } catch (
        error
      ) {
        /*
          DO NOT show employee "Unable to Start Work"
          because only history logging failed.
        */

        console.error(
          "Attendance history save error:",
          error
        );
      }
    };

  /*
    HEARTBEAT

    Every 60 seconds while Working.
  */

  useEffect(() => {
    if (
      !employee?.id ||
      workStatus !==
        "Working"
    ) {
      return undefined;
    }

    const heartbeat =
      async () => {
        const activityTime =
          new Date()
            .toISOString();

        try {
          await updateEmployeeAttendance({
            lastActivityAt:
              activityTime,
          });

          setLastActivityAt(
            activityTime
          );
        } catch (
          error
        ) {
          console.error(
            "Heartbeat error:",
            error
          );
        }
      };

    const timer =
      setInterval(
        heartbeat,
        60000
      );

    return () =>
      clearInterval(
        timer
      );
  }, [
    employee,
    workStatus,
    updateEmployeeAttendance,
  ]);

  /*
    START WORK
  */

  const handleStartWork =
    async () => {
      if (
        !employee?.id
      ) {
        return;
      }

      try {
        setAttendanceLoading(
          true
        );

        setAttendanceMessage(
          ""
        );

        const activityTime =
          new Date()
            .toISOString();

        const attendanceData =
          {
            workStatus:
              "Working",

            workStartedAt:
              activityTime,

            workEndedAt:
              "",

            breakStartedAt:
              "",

            breakEndedAt:
              "",

            totalBreakSeconds:
              0,

            lastActivityAt:
              activityTime,
          };

        /*
          STEP 1:
          Save MAIN attendance.
        */

        await updateEmployeeAttendance(
          attendanceData
        );

        /*
          STEP 2:
          Immediately update UI.
          Refresh is NOT required.
        */

        applyAttendanceState(
          attendanceData
        );

        setAttendanceMessage(
          "Work started successfully."
        );

        /*
          STEP 3:
          History is secondary.
          Failure here does not fail Start Work.
        */

        await saveAttendanceHistory({
          status:
            "Working",

          activityTime,

          finalBreakSeconds:
            0,
        });
      } catch (
        error
      ) {
        console.error(
          "Start work error:",
          error
        );

        setAttendanceMessage(
          "Unable to start work."
        );
      } finally {
        setAttendanceLoading(
          false
        );
      }
    };

  /*
    START BREAK
  */

  const handleStartBreak =
    async () => {
      if (
        !employee?.id ||
        workStatus !==
          "Working"
      ) {
        return;
      }

      try {
        setAttendanceLoading(
          true
        );

        setAttendanceMessage(
          ""
        );

        const activityTime =
          new Date()
            .toISOString();

        await updateEmployeeAttendance({
          workStatus:
            "On Break",

          breakStartedAt:
            activityTime,

          lastActivityAt:
            activityTime,
        });

        /*
          UI immediately changes.
        */

        setWorkStatus(
          "On Break"
        );

        setBreakStartedAt(
          activityTime
        );

        setLastActivityAt(
          activityTime
        );

        setAttendanceMessage(
          "Break started."
        );

        await saveAttendanceHistory({
          status:
            "On Break",

          activityTime,

          finalBreakSeconds:
            totalBreakSeconds,
        });
      } catch (
        error
      ) {
        console.error(
          "Start break error:",
          error
        );

        setAttendanceMessage(
          "Unable to start break."
        );
      } finally {
        setAttendanceLoading(
          false
        );
      }
    };

  /*
    END BREAK
  */

  const handleEndBreak =
    async () => {
      if (
        !employee?.id ||
        workStatus !==
          "On Break" ||
        !breakStartedAt
      ) {
        return;
      }

      try {
        setAttendanceLoading(
          true
        );

        setAttendanceMessage(
          ""
        );

        const currentDate =
          new Date();

        const activityTime =
          currentDate.toISOString();

        const breakStart =
          new Date(
            breakStartedAt
          );

        const breakDuration =
          Math.max(
            0,
            Math.floor(
              (
                currentDate.getTime() -
                breakStart.getTime()
              ) /
                1000
            )
          );

        const newBreakTotal =
          totalBreakSeconds +
          breakDuration;

        const breakRecord =
          {
            startedAt:
              breakStartedAt,

            endedAt:
              activityTime,

            durationSeconds:
              breakDuration,
          };

        await updateEmployeeAttendance({
          workStatus:
            "Working",

          breakStartedAt:
            "",

          breakEndedAt:
            activityTime,

          totalBreakSeconds:
            newBreakTotal,

          lastActivityAt:
            activityTime,
        });

        /*
          UI changes immediately.
        */

        setWorkStatus(
          "Working"
        );

        setBreakStartedAt(
          ""
        );

        setTotalBreakSeconds(
          newBreakTotal
        );

        setLastActivityAt(
          activityTime
        );

        setAttendanceMessage(
          "Break ended. Work continued."
        );

        await saveAttendanceHistory({
          status:
            "Working",

          activityTime,

          breakRecord,

          finalBreakSeconds:
            newBreakTotal,
        });
      } catch (
        error
      ) {
        console.error(
          "End break error:",
          error
        );

        setAttendanceMessage(
          "Unable to end break."
        );
      } finally {
        setAttendanceLoading(
          false
        );
      }
    };

  /*
    END WORK
  */

  const handleEndWork =
    async () => {
      if (
        !employee?.id ||
        (
          workStatus !==
            "Working" &&
          workStatus !==
            "On Break"
        )
      ) {
        return;
      }

      const confirmed =
        window.confirm(
          "Are you sure you want to end work?"
        );

      if (
        !confirmed
      ) {
        return;
      }

      try {
        setAttendanceLoading(
          true
        );

        setAttendanceMessage(
          ""
        );

        const currentDate =
          new Date();

        const activityTime =
          currentDate.toISOString();

        let finalBreakSeconds =
          totalBreakSeconds;

        let finalBreakRecord =
          null;

        /*
          If employee presses End Work
          while still on break,
          automatically close that break.
        */

        if (
          workStatus ===
            "On Break" &&
          breakStartedAt
        ) {
          const start =
            new Date(
              breakStartedAt
            );

          const duration =
            Math.max(
              0,
              Math.floor(
                (
                  currentDate.getTime() -
                  start.getTime()
                ) /
                  1000
              )
            );

          finalBreakSeconds +=
            duration;

          finalBreakRecord =
            {
              startedAt:
                breakStartedAt,

              endedAt:
                activityTime,

              durationSeconds:
                duration,
            };
        }

        /*
          FIRST SAVE MAIN STATUS.
        */

        await updateEmployeeAttendance({
          workStatus:
            "Work Ended",

          workEndedAt:
            activityTime,

          breakStartedAt:
            "",

          breakEndedAt:
            finalBreakRecord
              ? activityTime
              : "",

          totalBreakSeconds:
            finalBreakSeconds,

          lastActivityAt:
            activityTime,
        });

        /*
          IMMEDIATE UI UPDATE.
        */

        setWorkStatus(
          "Work Ended"
        );

        setWorkEndedAt(
          activityTime
        );

        setBreakStartedAt(
          ""
        );

        setTotalBreakSeconds(
          finalBreakSeconds
        );

        setLastActivityAt(
          activityTime
        );

        setAttendanceMessage(
          "Work ended successfully."
        );

        /*
          HISTORY SAVE IS SECONDARY.
        */

        await saveAttendanceHistory({
          status:
            "Work Ended",

          activityTime,

          breakRecord:
            finalBreakRecord,

          finalBreakSeconds,
        });
      } catch (
        error
      ) {
        console.error(
          "End work error:",
          error
        );

        setAttendanceMessage(
          "Unable to end work."
        );
      } finally {
        setAttendanceLoading(
          false
        );
      }
    };

  /*
    OPEN CALLS
  */

  const handleOpenCalls =
    async () => {
      if (
        !employee?.id ||
        workStatus !==
          "Working"
      ) {
        setAttendanceMessage(
          "Start work before opening calls."
        );

        return;
      }

      const activityTime =
        new Date()
          .toISOString();

      try {
        await updateEmployeeAttendance({
          lastActivityAt:
            activityTime,
        });

        setLastActivityAt(
          activityTime
        );
      } catch (
        error
      ) {
        console.error(
          "Activity update error:",
          error
        );
      }

      navigate(
        "/employee-calls"
      );
    };

  /*
    LOGOUT
  */

  const handleLogout =
    async () => {
      const shouldLogout =
        window.confirm(
          workStatus ===
              "Working" ||
            workStatus ===
              "On Break"
            ? "Work is still active. Logout without ending work?"
            : "Logout from your account?"
        );

      if (
        !shouldLogout
      ) {
        return;
      }

      try {
        await signOut(
          auth
        );
      } catch (
        error
      ) {
        console.error(
          "Logout error:",
          error
        );
      } finally {
        localStorage.removeItem(
          "crmEmployee"
        );

        navigate(
          "/employee-login"
        );
      }
    };

  /*
    DASHBOARD COUNTS
  */

  const todayDate =
    new Date()
      .toISOString()
      .split("T")[0];

  const getLeadContactDate =
    (
      lead
    ) => {
      if (
        !lead.lastContactedAt
      ) {
        return "";
      }

      try {
        if (
          typeof lead.lastContactedAt
            .toDate ===
          "function"
        ) {
          return lead.lastContactedAt
            .toDate()
            .toISOString()
            .split("T")[0];
        }

        const date =
          new Date(
            lead.lastContactedAt
          );

        if (
          Number.isNaN(
            date.getTime()
          )
        ) {
          return "";
        }

        return date
          .toISOString()
          .split("T")[0];
      } catch (
        error
      ) {
        return "";
      }
    };

  const dashboardCounts =
    useMemo(() => {
      const totalAssigned =
        leads.length;

      const callsCompletedToday =
        leads.filter(
          (lead) =>
            getLeadContactDate(
              lead
            ) ===
            todayDate
        ).length;

      const pendingLeads =
        leads.filter(
          (lead) =>
            lead.status !==
              "Converted" &&
            lead.status !==
              "Not Interested" &&
            lead.status !==
              "Wrong Number" &&
            lead.status !==
              "Do Not Call"
        ).length;

      const callbacksToday =
        leads.filter(
          (lead) =>
            lead.callbackStatus ===
              "Pending" &&
            lead.callbackDate ===
              todayDate
        ).length;

      const pendingCallbacks =
        leads.filter(
          (lead) =>
            lead.callbackStatus ===
              "Pending" ||
            lead.status ===
              "Callback Required"
        ).length;

      const missedCallbacks =
        leads.filter(
          (lead) =>
            lead.callbackStatus ===
              "Pending" &&
            lead.callbackDate &&
            lead.callbackDate <
              todayDate
        ).length;

      const interested =
        leads.filter(
          (lead) =>
            lead.status ===
              "Interested" ||
            lead.status ===
              "Follow-up"
        ).length;

      const converted =
        leads.filter(
          (lead) =>
            lead.status ===
            "Converted"
        ).length;

      return {
        totalAssigned,
        callsCompletedToday,
        pendingLeads,
        callbacksToday,
        pendingCallbacks,
        missedCallbacks,
        interested,
        converted,
      };
    }, [
      leads,
      todayDate,
    ]);

  /*
    TIME DISPLAY
  */

  const formatTime = (
    value
  ) => {
    if (
      !value
    ) {
      return "-";
    }

    const date =
      new Date(
        value
      );

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return "-";
    }

    return date.toLocaleTimeString(
      [],
      {
        hour:
          "2-digit",

        minute:
          "2-digit",
      }
    );
  };

  const formatDuration = (
    seconds
  ) => {
    const totalSeconds =
      Math.max(
        0,
        Math.floor(
          Number(
            seconds
          ) || 0
        )
      );

    const hours =
      Math.floor(
        totalSeconds /
          3600
      );

    const minutes =
      Math.floor(
        (
          totalSeconds %
          3600
        ) /
          60
      );

    if (
      hours > 0
    ) {
      return `${hours}h ${minutes}m`;
    }

    return `${minutes}m`;
  };

  const getCurrentBreakSeconds =
    () => {
      if (
        workStatus !==
          "On Break" ||
        !breakStartedAt
      ) {
        return 0;
      }

      return Math.max(
        0,
        Math.floor(
          (
            currentTime.getTime() -
            new Date(
              breakStartedAt
            ).getTime()
          ) /
            1000
        )
      );
    };

  const getElapsedWorkSeconds =
    () => {
      if (
        !workStartedAt
      ) {
        return 0;
      }

      const start =
        new Date(
          workStartedAt
        );

      const end =
        workStatus ===
            "Work Ended" &&
        workEndedAt
          ? new Date(
              workEndedAt
            )
          : currentTime;

      return Math.max(
        0,
        Math.floor(
          (
            end.getTime() -
            start.getTime()
          ) /
            1000
        )
      );
    };

  const displayedBreakSeconds =
    totalBreakSeconds +
    getCurrentBreakSeconds();

  const effectiveWorkedSeconds =
    Math.max(
      0,
      getElapsedWorkSeconds() -
        displayedBreakSeconds
    );

  const employeeStatCards =
    [
      {
        title:
          "Total Leads",

        value:
          dashboardCounts.totalAssigned,

        subtitle:
          "All assigned customers",

        reportType:
          "leads",
      },

      {
        title:
          "Calls Done Today",

        value:
          dashboardCounts.callsCompletedToday,

        subtitle:
          "Customers updated today",

        reportType:
          "calls",
      },

      {
        title:
          "Pending Leads",

        value:
          dashboardCounts.pendingLeads,

        subtitle:
          "Still requiring action",

        reportType:
          "pending",
      },

      {
        title:
          "Today’s Callbacks",

        value:
          dashboardCounts.callbacksToday,

        subtitle:
          "Scheduled for today",

        reportType:
          "today-callbacks",
      },

      {
        title:
          "Pending Callbacks",

        value:
          dashboardCounts.pendingCallbacks,

        subtitle:
          "Incomplete callbacks",

        reportType:
          "callbacks",
      },

      {
        title:
          "Missed Callbacks",

        value:
          dashboardCounts.missedCallbacks,

        subtitle:
          "Callback date passed",

        reportType:
          "missed",
      },

      {
        title:
          "Interested Leads",

        value:
          dashboardCounts.interested,

        subtitle:
          "Interested customers",

        reportType:
          "interested",
      },

      {
        title:
          "Converted Leads",

        value:
          dashboardCounts.converted,

        subtitle:
          "Successful conversions",

        reportType:
          "converted",
      },
    ];

  return (
    <div className="admin-dashboard-page employee-dashboard-page">

      <header className="dashboard-header">

        <div>

          <h1>
            Trading Wealth CRM
          </h1>

          <p>
            Employee Dashboard
          </p>

        </div>

        <button
          type="button"
          className="logout-button"
          onClick={
            handleLogout
          }
        >
          Account Logout
        </button>

      </header>

      <main className="dashboard-main">

        <section className="dashboard-welcome">

          <p className="dashboard-label">
            EMPLOYEE WORKSPACE
          </p>

          <h2>
            Welcome,{" "}
            {employee?.name ||
              "Employee"}
          </h2>

          <p>
            Start work, complete calls, enter remarks and manage callbacks.
          </p>

        </section>

        <section className="attendance-card">

          <div className="attendance-top">

            <div>

              <p className="dashboard-label">
                WORK STATUS
              </p>

              <h2>
                {workStatus}
              </h2>

            </div>

            <span
              className={
                workStatus ===
                "Working"
                  ? "attendance-status working"
                  : workStatus ===
                    "On Break"
                    ? "attendance-status break"
                    : "attendance-status stopped"
              }
            >
              {workStatus}
            </span>

          </div>

          <div className="attendance-time-row">

            <div>

              <span>
                Work Started
              </span>

              <strong>
                {formatTime(
                  workStartedAt
                )}
              </strong>

            </div>

            <div>

              <span>
                Work Ended
              </span>

              <strong>
                {formatTime(
                  workEndedAt
                )}
              </strong>

            </div>

            <div>

              <span>
                Total Break
              </span>

              <strong>
                {formatDuration(
                  displayedBreakSeconds
                )}
              </strong>

            </div>

            <div>

              <span>
                Effective Work
              </span>

              <strong>
                {formatDuration(
                  effectiveWorkedSeconds
                )}
              </strong>

            </div>

            <div>

              <span>
                Last Activity
              </span>

              <strong>
                {formatTime(
                  lastActivityAt
                )}
              </strong>

            </div>

          </div>

          <div className="attendance-actions">

            {(
              workStatus ===
                "Not Started" ||
              workStatus ===
                "Work Ended"
            ) && (

              <button
                type="button"
                className="attendance-button start"
                onClick={
                  handleStartWork
                }
                disabled={
                  attendanceLoading
                }
              >
                {attendanceLoading
                  ? "Please wait..."
                  : "Start Work"}
              </button>

            )}

            {workStatus ===
              "Working" && (

              <>

                <button
                  type="button"
                  className="attendance-button break"
                  onClick={
                    handleStartBreak
                  }
                  disabled={
                    attendanceLoading
                  }
                >
                  Start Break
                </button>

                <button
                  type="button"
                  className="attendance-button end"
                  onClick={
                    handleEndWork
                  }
                  disabled={
                    attendanceLoading
                  }
                >
                  End Work
                </button>

              </>

            )}

            {workStatus ===
              "On Break" && (

              <>

                <button
                  type="button"
                  className="attendance-button start"
                  onClick={
                    handleEndBreak
                  }
                  disabled={
                    attendanceLoading
                  }
                >
                  End Break
                </button>

                <button
                  type="button"
                  className="attendance-button end"
                  onClick={
                    handleEndWork
                  }
                  disabled={
                    attendanceLoading
                  }
                >
                  End Work
                </button>

              </>

            )}

          </div>

          {attendanceMessage && (

            <div className="attendance-message">
              {attendanceMessage}
            </div>

          )}

        </section>

        <section className="dashboard-stats employee-dashboard-stats">

          {employeeStatCards.map(
            (
              card
            ) => (

              <div
                key={
                  card.reportType
                }
                className="dashboard-stat-card"
                role="button"
                tabIndex="0"
                onClick={() =>
                  navigate(
                    `/employee-reports?type=${card.reportType}`
                  )
                }
                onKeyDown={(
                  event
                ) => {
                  if (
                    event.key ===
                      "Enter" ||
                    event.key ===
                      " "
                  ) {
                    navigate(
                      `/employee-reports?type=${card.reportType}`
                    );
                  }
                }}
                style={{
                  cursor:
                    "pointer",
                }}
              >

                <p>
                  {card.title}
                </p>

                <h3>
                  {loading
                    ? "..."
                    : card.value}
                </h3>

                <span>
                  {card.subtitle}
                </span>

              </div>

            )
          )}

        </section>

        <section className="dashboard-section">

          <div className="section-heading">

            <div>

              <p className="dashboard-label">
                START CALLING
              </p>

              <h2>
                Manage customer calls
              </h2>

            </div>

          </div>

          <button
            type="button"
            className="employee-main-action-button"
            onClick={
              handleOpenCalls
            }
            disabled={
              workStatus !==
              "Working"
            }
          >

            <div>

              <strong>
                {workStatus ===
                "Working"
                  ? "Open My Calls"
                  : "Start Work to Open Calls"}
              </strong>

              <span>
                Call customers, enter remarks and manage callbacks
              </span>

            </div>

            <span className="employee-main-action-arrow">
              →
            </span>

          </button>

        </section>

      </main>

    </div>
  );
}

export default EmployeeDashboard;